import { useEffect, useState } from 'react';
import { CodigoQr } from '../componentes/CodigoQr';
import { Icono } from '../componentes/Icono';
import type { Nota } from './carteras';
import appleEn from '../estilo/comun/assets/wallet/apple-wallet-en.svg';
import appleEs from '../estilo/comun/assets/wallet/apple-wallet-es.svg';
import googleEnBoton from '../estilo/comun/assets/wallet/google-wallet-en-boton.svg';
import googleEnInsignia from '../estilo/comun/assets/wallet/google-wallet-en-insignia.svg';
import googleEsBoton from '../estilo/comun/assets/wallet/google-wallet-es-boton.svg';
import googleEsInsignia from '../estilo/comun/assets/wallet/google-wallet-es-insignia.svg';
import { codigoEnGrupos } from './pases';
import './pases.css';

/**
 * Piezas comunes de la página del pase (6.6) y de «Mis pases» (6.7): el QR con su código, «Ampliar» a pantalla completa
 * (la pantalla no se apaga mientras se enseña), los botones de las carteras (6.8, G50) y la nota bajo las acciones.
 *
 * Insignias: las oficiales de Apple Wallet y Google Wallet (C.23, G927, G928).
 */

export function NotaPase({ nota }: { nota: Nota | null }) {
    if (nota === null) {
        return null;
    }
    return (
        <p role="status" className={`rv-ps-nota rv-ps-nota--${nota[1]}`}>
            <Icono nombre={nota[0]} />
            <span>{nota[2]}</span>
        </p>
    );
}

/** La pantalla encendida mientras se enseña el QR (Screen Wake Lock; si el navegador no lo tiene, no pasa nada). */
function usePantallaEncendida(activa: boolean) {
    useEffect(() => {
        if (!activa) {
            return;
        }
        let candado: WakeLockSentinel | null = null;
        let vivo = true;
        if ('wakeLock' in navigator) {
            navigator.wakeLock
                .request('screen')
                .then((c) => {
                    if (vivo) {
                        candado = c;
                    } else {
                        void c.release().catch(() => undefined);
                    }
                })
                .catch(() => undefined);
        }
        return () => {
            vivo = false;
            void candado?.release().catch(() => undefined);
        };
    }, [activa]);
}

export function BloqueQr({ qr, codigo, etiqueta, tamano = 248, usado = false, ampliable = true }: { qr: string; codigo: string; etiqueta: string; tamano?: number; usado?: boolean; ampliable?: boolean }) {
    const [ampliado, setAmpliado] = useState(false);
    usePantallaEncendida(ampliado);
    useEffect(() => {
        if (!ampliado) {
            return;
        }
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setAmpliado(false);
            }
        };
        window.addEventListener('keydown', tecla);
        return () => {
            window.removeEventListener('keydown', tecla);
        };
    }, [ampliado]);
    return (
        <div className="rv-ps-qr">
            <div className={`rv-ps-qr__marco${usado ? ' rv-ps-qr__marco--usado' : ''}`}>
                <CodigoQr valor={qr} etiqueta={etiqueta} tamano={tamano} />
                {usado && (
                    <span className="rv-ps-qr__sello">
                        <Icono nombre="door-open" />
                        Ya usado
                    </span>
                )}
            </div>
            <span className="rv-ps-qr__ayuda">{usado ? 'Código' : 'Si no se puede leer, di este código'}</span>
            <strong className="rv-ps-qr__codigo">{codigoEnGrupos(codigo)}</strong>
            {ampliable && !usado && (
                <div className="rv-ps-brillo">
                    <Icono nombre="sun" />
                    <span>En la puerta, sube el brillo de la pantalla.</span>
                    <button type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={() => { setAmpliado(true); }}>
                        <Icono nombre="maximize-2" />
                        <span>Ampliar</span>
                    </button>
                </div>
            )}
            {ampliado && (
                <div className="rv-ps-ampliado" role="dialog" aria-modal="true" aria-label="QR a pantalla completa">
                    <CodigoQr valor={qr} etiqueta={etiqueta} tamano={Math.min(420, typeof window === 'undefined' ? 360 : window.innerWidth - 32)} />
                    <strong className="rv-ps-qr__codigo">{codigoEnGrupos(codigo)}</strong>
                    <span>Sube el brillo de la pantalla.</span>
                    <button type="button" className="rv-pc-b rv-pc-b--sec" onClick={() => { setAmpliado(false); }}>
                        <Icono nombre="x" />
                        <span>Cerrar</span>
                    </button>
                </div>
            )}
        </div>
    );
}

/** Textos de las insignias (G927, G928): español por defecto; inglés donde la página va en inglés. */
const CARTERAS = {
    es: { apple: appleEs, googleAncha: googleEsBoton, googleEstrecha: googleEsInsignia, textoApple: 'Añadir a Cartera de Apple', textoGoogle: 'Añadir a Google Wallet' },
    en: { apple: appleEn, googleAncha: googleEnBoton, googleEstrecha: googleEnInsignia, textoApple: 'Add to Apple Wallet', textoGoogle: 'Add to Google Wallet' },
} as const;

/**
 * Las insignias oficiales de Apple Wallet y Google Wallet (C.23, `rv-cartera` de `base.css`): los SVG de
 * `estilo/comun/assets/wallet/` enteros, a 44 px de alto, con 12 px entre las dos y una encima de la otra si no caben. La de
 * Google: el botón de una línea si cabe junto a la de Apple y, con poco ancho, la insignia de dos líneas (las dos van en el
 * botón y `base.css` enseña una u otra según el ancho del contenedor). Son botones porque Apple baja un archivo y Google pide
 * su enlace a la API (6.8).
 */
export function BotonesCartera({ apple, google, ocupada, onApple, onGoogle, idioma = 'es' }: { apple: boolean; google: boolean; ocupada: 'apple' | 'google' | null; onApple: () => void; onGoogle: () => void; idioma?: 'es' | 'en' }) {
    if (!apple && !google) {
        return null;
    }
    const c = CARTERAS[idioma];
    return (
        <div className="rv-carteras">
            {apple && (
                <button type="button" className="rv-cartera" aria-busy={ocupada === 'apple'} disabled={ocupada !== null} onClick={onApple}>
                    <img src={c.apple} alt={c.textoApple} />
                </button>
            )}
            {google && (
                <button type="button" className="rv-cartera" aria-busy={ocupada === 'google'} disabled={ocupada !== null} onClick={onGoogle} aria-label={c.textoGoogle}>
                    <img className="rv-cartera-ancha" src={c.googleAncha} alt="" />
                    <img className="rv-cartera-estrecha" src={c.googleEstrecha} alt="" />
                </button>
            )}
        </div>
    );
}
